import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ExternalLink, Youtube } from "lucide-react";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import { PlayableCover, WatchOnYouTube } from "@/components/ReleasePlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StreamPill = ({ href, label, testid, icon }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className="inline-flex items-center gap-1.5 text-[11px] font-meta tracking-wider uppercase text-amber-200/90 border border-amber-500/25 rounded-full px-3 py-1.5 hover:bg-amber-500 hover:text-black hover:border-transparent transition-all duration-200"
    >
        {icon}
        {label}
        <ExternalLink size={11} />
    </a>
);

const ReleaseLinks = ({ release }) => {
    const { links, slug } = release;
    return (
        <div className="flex flex-wrap gap-2 mt-5">
            {links.spotify && <StreamPill href={links.spotify} label="Spotify" testid={`music-spotify-link-${slug}`} />}
            {links.apple && <StreamPill href={links.apple} label="Apple Music" testid={`music-apple-link-${slug}`} />}
            {links.amazon && <StreamPill href={links.amazon} label="Amazon" testid={`music-amazon-link-${slug}`} />}
            {links.youtube && (
                <StreamPill href={links.youtube} label="YouTube" testid={`music-youtube-link-${slug}`} icon={<Youtube size={12} />} />
            )}
        </div>
    );
};

const ReleaseCard = ({ release, index, playing, onTogglePlay }) => (
    <motion.article
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
        data-testid={`music-album-card-${release.slug}`}
        className="group bg-zinc-900/60 backdrop-blur-md border border-amber-500/15 hover:border-amber-400/50 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_16px_40px_rgba(0,0,0,0.7)]"
    >
        <div className="relative overflow-hidden aspect-square">
            <PlayableCover release={release} open={playing} onToggle={onTogglePlay} />
            {!playing && (
                <span className="absolute top-4 left-4 font-meta text-[10px] tracking-[0.25em] uppercase bg-black/70 backdrop-blur px-3 py-1.5 rounded-full text-amber-300 border border-amber-500/30">
                    {release.status}
                </span>
            )}
        </div>
        <div className="p-5 sm:p-6">
            <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-display text-lg font-bold text-white">{release.title}</h3>
                {release.tracks && <span className="font-meta text-[11px] text-zinc-500">{release.tracks} tracks</span>}
            </div>
            {release.description && (
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed" data-testid={`music-description-${release.slug}`}>
                    {release.description}
                </p>
            )}
            <ReleaseLinks release={release} />
            {playing && <WatchOnYouTube release={release} />}
        </div>
    </motion.article>
);

const ReleaseSection = ({ title, testid, releases, playingId, onTogglePlay }) =>
    releases.length > 0 && (
        <section data-testid={testid} className="px-5 sm:px-8 pb-20">
            <div className="max-w-7xl mx-auto">
                <FadeIn className="mb-8">
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-white flex items-center gap-4">
                        {title}
                        <span className="font-meta text-[11px] tracking-[0.3em] text-amber-500/70 font-normal">
                            {String(releases.length).padStart(2, "0")}
                        </span>
                        <span className="flex-1 h-px bg-gradient-to-r from-amber-500/40 to-transparent" />
                    </h2>
                </FadeIn>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {releases.map((r, i) => (
                        <ReleaseCard
                            key={r.id}
                            release={r}
                            index={i}
                            playing={playingId === r.id}
                            onTogglePlay={() => onTogglePlay(r.id)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );

const Music = () => {
    const [releases, setReleases] = useState(null);
    const [playingId, setPlayingId] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/releases`)
            .then((res) => setReleases(res.data))
            .catch(() => setReleases([]));
    }, []);

    const toggle = (id) => setPlayingId((current) => (current === id ? null : id));
    const albums = (releases || []).filter((r) => r.type === "Album");
    const singles = (releases || []).filter((r) => r.type === "Single");

    return (
        <div data-testid="music-page" className="pt-16 sm:pt-[72px]">
            <section className="relative px-5 sm:px-8 pt-20 pb-14 overflow-hidden">
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[80vw] h-[50vh] bg-amber-500/10 blur-[130px] rounded-full pointer-events-none" />
                <div className="max-w-7xl mx-auto">
                    <SectionHeading eyebrow="The Sound" title="Discography" />
                    <FadeIn delay={0.1}>
                        <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed">
                            Every project a chapter of the journey. All releases out now.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {releases === null ? (
                <div className="px-5 sm:px-8 pb-24">
                    <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="rounded-2xl border border-amber-500/10 bg-zinc-900/40 aspect-[3/4] animate-pulse" />
                        ))}
                    </div>
                </div>
            ) : (
                <>
                    <ReleaseSection
                        title="Albums"
                        testid="music-section-albums"
                        releases={albums}
                        playingId={playingId}
                        onTogglePlay={toggle}
                    />
                    <ReleaseSection
                        title="Singles"
                        testid="music-section-singles"
                        releases={singles}
                        playingId={playingId}
                        onTogglePlay={toggle}
                    />
                </>
            )}
        </div>
    );
};

export default Music;
